import { ChannelType, CompanyType, EConsentDetailsModel, Signer, SignerModel } from "./types";

export function mapToColumnModel(data: EConsentDetailsModel[]): Record<string, any>[] {
  return data.map((item) => {
    const { signer } = item;
    const link = [
      signer?.physician?.link,
      signer?.patient?.link,
      signer?.witness?.link,
      signer?.witness2?.link,
      signer?.interpreter?.link,
      ].filter((link) => link?.trim() && link.trim() !== "-").at(-1) ?? "";

    return {
      appointmentDateTime: item.appointmentDateTime ?? "",
      remainingDays: calculatedAppointmentDateDiff(item.appointmentDateTime),
      hn: item.hn ?? "",
      fullName: item.fullName ?? "",
      fullNameEn: item.fullNameEn || (item.fullNameTh ? null : item.fullName) || "",
      fullNameTh: item.fullNameTh ?? "",
      status: item.status ?? "",
      templateName: item.templateName ?? "",
      transactionId: item.transactionId ?? "",
      salesforceCaseAppointmentId: item.salesforceCaseAppointmentId ?? "",
      email: item.email ?? "",
      nationalID: item.nationalID ?? "",
      passportNo: item.passportNo ?? "",
      birthDate: item.birthDate ?? "",
      voidedReason: item.voidedReason ?? "",
      patientSubmittedAt: item.patientSubmittedAt ? `${item.patientSubmittedAt}Z`: "",
      witnessSubmittedAt: item.witnessSubmittedAt ? `${item.witnessSubmittedAt}Z`: "",
      eSignatureLink: item.eSignatureLink && item.eSignatureLink !== "-" ? item.eSignatureLink : link || "-",
      in_person_signing: "",
      cancelledBy: item.cancelledBy ?? "",
      channel: mapToChannelType(item.channel),
      company: mapToCompanyType(item.company),
      createdAt: item.createdAt ? `${item.createdAt}Z`: "",
      createdBy: item.createdBy ?? "",
      delegateTo: item.delegateTo ?? "",
      delegatedAt: item.delegatedAt ?? "",
      delegateHistories: item.delegateHistories,
      note: item.note ?? "",
      witness: mapToSignerModel(signer?.witness),
    };
  });
}

export function mapToClinicalColumnModel(data: EConsentDetailsModel[]): Record<string, any>[] {
  return data.map((item) => {
    const { signer } = item;
    return {
      appointmentDateTime: item.appointmentDateTime ?? "",
      remainingDays: calculatedAppointmentDateDiff(item.appointmentDateTime),
      hn: item.hn ?? "",
      fullName: item.fullName ?? "",
      fullNameEn: item.fullNameEn || (item.fullNameTh ? null : item.fullName) || "",
      fullNameTh: item.fullNameTh ?? "",
      status: item.status ?? "",
      templateName: item.templateName ?? "",
      templateLanguage: item.templateLanguage ?? "",
      transactionId: item.transactionId ?? "",
      salesforceCaseAppointmentId: item.salesforceCaseAppointmentId ?? "",
      email: item.email ?? "",
      nationalID: item.nationalID ?? "",
      passportNo: item.passportNo ?? "",
      birthDate: item.birthDate ?? "",
      voidedReason: item.voidedReason ?? "",
      patientSubmittedAt: item.patientSubmittedAt ?? "",
      witnessSubmittedAt: item.witnessSubmittedAt ?? "",
      eSignatureLink: `/transaction/${item.transactionId}`,
      in_person_signing: "",
      cancelledBy: item.cancelledBy ?? "",
      channel: mapToChannelType(item.channel),
      company: mapToCompanyType(item.company),
      createdAt: item.createdAt ? `${item.createdAt}Z`: "",
      createdBy: item.createdBy ?? "",
      delegateTo: item.delegateTo ?? "",
      note: item.note ?? "",
      urgentCase: item.urgentCase
        ? {
            flag: item.urgentCase.flag === true,
            at: item.urgentCase.at ?? null,
            by: item.urgentCase.by ?? null,
          }
        : null,
      physician: mapToSignerModel(signer?.physician),
      patient: mapToSignerModel(signer?.patient),
      witness: mapToSignerModel(signer?.witness),
      witness2: mapToSignerModel(signer?.witness2),
      interpreter: mapToSignerModel(signer?.interpreter),
      locationLogon: item.locationLogon?.name ?? "",
    };
  });
}

export function mapToSignerModel(signer?: Signer | null): SignerModel | null {
  if (!signer) return null;

  return {
    id: signer.candidateId,
    name: signer.name,
    status: signer.status,
    link: signer.link
  };
}

export function mapToChannelType(channel: string | null): ChannelType {
  switch (channel) {
    case ChannelType.SALEFORCE: return ChannelType.SALEFORCE
    case ChannelType.IPS: return ChannelType.IPS
    case ChannelType.TRAKCARE: return ChannelType.TRAKCARE
    default: return ChannelType.NONE
  }
}

export function mapToCompanyType(company: string | null): CompanyType {
  switch (company) {
    case CompanyType.BUMRUNGRAD: return CompanyType.BUMRUNGRAD
    case CompanyType.VITALLIFE: return CompanyType.VITALLIFE
    case CompanyType.ESPERANCE: return CompanyType.ESPERANCE
    default: return CompanyType.NONE
  }
}

function calculatedAppointmentDateDiff(appointmentDate: string | null): string {
  if (appointmentDate === null) return '-999';
  const date1: Date = new Date(appointmentDate);
  const date2: Date = new Date();
  const diffTime = date1.getTime() - date2.getTime();
  const diffDays = Math.trunc(diffTime / (1000 * 60 * 60 * 24));
  return String(diffDays);
}